import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@/localization/useTranslation'
import { Button } from '@/components/Buttons/Button'
import { TranslationType } from '@/types/common'
import { findVisibleTarget, getAnchoredPanelLayout, isRoundTarget } from '@/utils/anchoredPanel'
import classes from './styles.module.scss'
import { Badge } from '../Badge'

interface SpotlightProps {
  targetId: string
  title: TranslationType
  description: TranslationType
  /** Primary label. Defaults to "Next" while more steps follow, "Got it" on the last. */
  actionText?: TranslationType
  /** Secondary, non-committal exit. Omitted renders no secondary button. */
  dismissText?: TranslationType
  hasNextStep?: boolean
  /** Spotlights are for identified users only (spec §4.4) — the caller passes the session gate. */
  enabled?: boolean
  /** Fired when the step actually appears, not when it is merely queued. */
  onShown?: () => void
  /** Fired when the target is not on this page, so the caller can move past it. */
  onUnavailable?: () => void
  onAction: () => void
  onDismiss?: () => void
}

/** The DOM needs a beat to settle before the target can be measured. */
const MEASURE_DELAY_MS = 500
/** Breathing room between the cutout and the highlighted element. */
const CUTOUT_PADDING = 8
/**
 * Round targets get more: the profile avatar carries its own emphasis ring, and
 * at the default padding the two circles read as one thick double border.
 */
const ROUND_CUTOUT_PADDING = 14
const POPUP_OFFSET = 16
const DEFAULT_CUTOUT_RADIUS = '4px'
const ROUND_CUTOUT_RADIUS = '50%'

export const Spotlight: React.FC<SpotlightProps> = (props) => {
  const {
    targetId,
    title,
    description,
    actionText,
    dismissText,
    hasNextStep = false,
    enabled = true,
    onShown,
    onUnavailable,
    onAction,
    onDismiss,
  } = props
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const [isRoundCutout, setIsRoundCutout] = useState(false)

  useEffect(() => {
    if (!enabled) return

    const timeoutId = setTimeout(() => {
      const element = findVisibleTarget(targetId)
      if (!element) {
        // Never shown, so nothing is recorded as answered — but the caller has
        // to hear about it, or a step with no target here blocks the ones
        // behind it for the whole page.
        onUnavailable?.()
        return
      }

      setTargetRect(element.getBoundingClientRect())
      setIsRoundCutout(isRoundTarget(element))
      setIsVisible(true)
      onShown?.()
    }, MEASURE_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [targetId, enabled, onShown, onUnavailable])

  useEffect(() => {
    if (!isVisible) return

    const handleResize = () => {
      const element = findVisibleTarget(targetId)
      if (element) {
        // Only the rect: re-reading the computed style here would force a style
        // recalc on every scroll event, and a target's shape cannot change mid-scroll.
        setTargetRect(element.getBoundingClientRect())
      }
    }

    window.addEventListener('resize', handleResize, { passive: true })
    window.addEventListener('scroll', handleResize, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize)
    }
  }, [isVisible, targetId])

  const handleAction = () => {
    setIsVisible(false)
    onAction()
  }

  const handleDismiss = () => {
    setIsVisible(false)
    onDismiss?.()
  }

  if (!isVisible || !targetRect) return null

  // Symmetric on both axes: a decoration drawn outside the target's box — the
  // avatar's completeness ring is a pseudo-element, so it never widens
  // `getBoundingClientRect` — used to spill past the bottom edge.
  const padding = isRoundCutout ? ROUND_CUTOUT_PADDING : CUTOUT_PADDING
  const cutoutStyle = {
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
    borderRadius: isRoundCutout ? ROUND_CUTOUT_RADIUS : DEFAULT_CUTOUT_RADIUS,
  }

  const { width, left, arrowLeft } = getAnchoredPanelLayout(targetRect, window.innerWidth)
  const popupStyle = {
    top: targetRect.bottom + padding + POPUP_OFFSET,
    left,
    width,
  }
  const primaryLabel = actionText ?? (hasNextStep ? 'Next' : 'Got it')

  return createPortal(
    <div className={classes.overlay}>
      <div className={classes.cutout} style={cutoutStyle} />

      <div className={classes.popup} style={popupStyle}>
        <div className={classes.arrow} style={{ left: arrowLeft }} />
        <Badge>{t('New')}</Badge>
        <h4 className={classes.title}>{t(title)}</h4>
        <p className={classes.description}>{t(description)}</p>
        <div className={classes.actions}>
          {dismissText && (
            <Button size="sm" variant="secondary" onClick={handleDismiss}>
              {t(dismissText)}
            </Button>
          )}
          <Button size="sm" onClick={handleAction}>
            {t(primaryLabel)}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
